import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { TrendingUp, TrendingDown, Minus, Clock } from "lucide-react";
import { ShimmerPanel } from "@/components/ui/Shimmer";

const ForecastPanel = ({ forecast, moodColor, loaded = false }) => {
  const { t } = useTranslation();
  const [displayData, setDisplayData] = useState(null);
  const hasLoadedOnce = useRef(false);

  // Update display data only when we have new valid data
  useEffect(() => {
    if (forecast?.forecast?.length) {
      setDisplayData(forecast);
      hasLoadedOnce.current = true;
    }
  }, [forecast]);
  
  // Show shimmer only while the very first data response is still pending.
  if (!displayData?.forecast?.length) {
    if (!hasLoadedOnce.current && !loaded) {
      return <ShimmerPanel type="forecast" />;
    }
    return (
      <div className="empty-state">
        <Clock size={24} style={{ marginBottom: 8, opacity: 0.5 }} />
        <span>{t('analyzing')}</span>
      </div>
    );
  }

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUp size={14} className="text-green-400" />;
    if (trend === 'worsening') return <TrendingDown size={14} className="text-red-400" />;
    return <Minus size={14} className="text-gray-400" />;
  };

  return (
    <div className="forecast-container" data-testid="forecast-panel">
      {/* Trend indicator */}
      <div className="forecast-trend mb-3">
        {getTrendIcon(displayData.overall_trend)}
        <span className="text-xs ml-1 capitalize" style={{ color: 'var(--apple-text-secondary)' }}>
          {displayData.overall_trend} trend
        </span>
      </div>

      {/* Forecast timeline */}
      <div className="forecast-timeline">
        {displayData.forecast.slice(0, 4).map((period, idx) => (
          <motion.div
            key={idx}
            className="forecast-period"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
          >
            <div className="forecast-time">{period.hours}</div>
            <div 
              className="forecast-color-dot"
              style={{ backgroundColor: period.color }}
            />
            <div className="forecast-mood" style={{ color: period.color }}>
              {period.adjective}
            </div>
            <div className="forecast-confidence">
              {Math.round((period.confidence || 0.5) * 100)}%
            </div>
          </motion.div>
        ))}
      </div>

      {/* Key factors */}
      {displayData.key_factors?.length > 0 && (
        <div className="forecast-factors mt-3">
          <div className="text-xs mb-1" style={{ color: 'var(--apple-text-tertiary)' }}>
            Key factors:
          </div>
          <div className="flex flex-wrap gap-1">
            {displayData.key_factors.slice(0, 3).map((factor, idx) => (
              <span key={idx} className="forecast-factor-tag">
                {factor}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { ForecastPanel };
